import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Search, ArrowLeft, Plus, ExternalLink } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { getStores, getStoreProducts, addStoreProduct, updateStoreProduct, deleteStoreProduct } from '../lib/storeService';
import { getRankedGames } from '../lib/game/searchService';
import SEO from '../components/SEO';
import useDebounce from '../hooks/useDebounce';
import { supabase } from '../lib/supabase';
import { FaSearch, FaEdit, FaTrash } from 'react-icons/fa';
import { Loader } from '../components/Loader';
import MainLayout from '../layouts/MainLayout';
import { formatCurrency } from '../utils/formatters';
import { useTheme } from '../contexts/ThemeContext';

interface GameOption {
  id: string;
  name: string;
}

interface StoreProduct {
  id: string;
  store_id: string;
  game_id: string;
  price: number | null;
  product_url: string;
  created_at: string;
  updated_at: string;
  available: boolean | null;
  product?: {
    id: string;
    name: string;
    image_url: string | null;
    slug: string | null;
  }
}

interface StoreInfo {
  id: string;
  name: string;
  logo_url: string | null;
  url: string;
}

const StoreProductsPage: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { theme } = useTheme();
  
  // Estado para os produtos e a loja
  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<StoreProduct[]>([]);
  const [store, setStore] = useState<StoreInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Estado para filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<StoreProduct | null>(null);
  
  // Estado para disponibilidade de jogos para seleção
  const [gameOptions, setGameOptions] = useState<GameOption[]>([]);
  const [gameSearchText, setGameSearchText] = useState('');
  const debouncedGameSearchText = useDebounce(gameSearchText, 500);
  const [loadingGames, setLoadingGames] = useState(false);
  
  // Estado para o formulário de produto
  const [formData, setFormData] = useState({
    game_id: '',
    product_url: '',
    price: '',
    available: true
  });
  
  // Estado para erros
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (storeId) {
      fetchStoreDetails();
      fetchStoreProducts();
    }
  }, [storeId]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = products.filter(product => 
        product.product?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products);
    }
  }, [searchTerm, products]);

  const fetchStoreDetails = async () => {
    if (!storeId) {
      setError('ID da loja não fornecido.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('stores')
        .select('id, name, logo_url, url')
        .eq('id', storeId)
        .single();

      if (error) throw error;
      
      setStore(data);
    } catch (err) {
      console.error('Erro ao buscar detalhes da loja:', err);
      setError('Não foi possível carregar os detalhes da loja.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreProducts = async () => {
    if (!storeId) {
      setError('ID da loja não fornecido para buscar produtos.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('store_products')
        .select(`
          id, 
          store_id, 
          game_id, 
          price, 
          product_url, 
          created_at, 
          updated_at, 
          available,
          product:game_id (
            id, 
            name, 
            image_url,
            slug
          )
        `)
        .eq('store_id', storeId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setProducts(data || []);
      setFilteredProducts(data || []);
    } catch (err) {
      console.error('Erro ao buscar produtos da loja:', err);
      setError('Não foi possível carregar a lista de produtos.');
    } finally {
      setLoading(false);
    }
  };

  // Buscar jogos para o dropdown do formulário
  const fetchGameOptions = useCallback(async () => {
    if (!debouncedGameSearchText) return;
    
    setLoadingGames(true);
    try {
      const response = await getRankedGames(
        { sortBy: 'name', ascending: true },
        { searchText: debouncedGameSearchText },
        1,
        20
      );
      
      const options = response.data.map(game => ({
        id: game.id,
        name: game.name
      }));
      
      setGameOptions(options);
    } catch (error) {
      console.error('Erro ao buscar jogos:', error);
    } finally {
      setLoadingGames(false);
    }
  }, [debouncedGameSearchText]);
  
  // Buscar opções de jogos quando necessário
  useEffect(() => {
    fetchGameOptions();
  }, [fetchGameOptions]);
  
  // Manipular mudança na página
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  
  // Manipular mudança no campo de busca
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Resetar para a primeira página ao buscar
  };
  
  // Manipular mudanças no formulário
  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      setFormData(prev => ({ 
        ...prev, 
        [name]: (e.target as HTMLInputElement).checked 
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  // Resetar o formulário
  const resetForm = () => {
    setFormData({
      game_id: '',
      product_url: '',
      price: '',
      available: true
    });
    setEditingProduct(null);
    setShowAddForm(false);
    setGameSearchText('');
    setGameOptions([]);
  };
  
  // Configurar o formulário para edição
  const setupEditForm = (product: StoreProduct) => {
    setEditingProduct(product);
    setFormData({
      game_id: product.game_id,
      product_url: product.product_url,
      price: product.price ? product.price.toString() : '',
      available: product.available ?? true
    });
    
    // Adicionar o jogo atual às opções se ele não estiver no resultado da busca
    if (product.product && product.product.id && product.product.name) {
      setGameOptions(prev => {
        if (!prev.some(option => option.id === product.product!.id)) {
          return [...prev, { id: product.product!.id, name: product.product!.name }];
        }
        return prev;
      });
    }
    
    setShowAddForm(true);
  };
  
  // Adicionar novo produto
  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!storeId || !formData.game_id || !formData.product_url) {
      toast.error('Jogo e URL do produto são obrigatórios');
      return;
    }
    
    try {
      const newProduct = await addStoreProduct({
        store_id: storeId,
        game_id: formData.game_id,
        product_url: formData.product_url,
        price: formData.price ? parseFloat(formData.price) : null,
        available: formData.available
      });
      
      // Atualizar a lista completa para incluir o novo produto
      fetchStoreProducts();
      toast.success('Produto adicionado com sucesso!');
      resetForm();
    } catch (error) {
      console.error('Erro ao adicionar produto:', error);
      toast.error('Falha ao adicionar produto. Tente novamente.');
    }
  };
  
  // Atualizar produto existente
  const handleUpdateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingProduct || !formData.product_url) {
      toast.error('URL do produto é obrigatória');
      return;
    }
    
    try {
      await updateStoreProduct(editingProduct.id, {
        product_url: formData.product_url,
        price: formData.price ? parseFloat(formData.price) : null,
        available: formData.available
      });
      
      // Atualizar a lista completa para refletir as alterações
      fetchStoreProducts();
      toast.success('Produto atualizado com sucesso!');
      resetForm();
    } catch (error) {
      console.error('Erro ao atualizar produto:', error);
      toast.error('Falha ao atualizar produto. Tente novamente.');
    }
  };
  
  // Excluir produto
  const handleDeleteProduct = async (e: React.MouseEvent, productId: string) => {
    e.preventDefault();
    if (window.confirm('Tem certeza que deseja excluir este produto?')) {
      try {
        const { error } = await supabase
          .from('store_products')
          .delete()
          .eq('id', productId);

        if (error) throw error;
        
        // Atualizar lista após exclusão
        setProducts(products.filter(product => product.id !== productId));
        setFilteredProducts(filteredProducts.filter(product => product.id !== productId));
      } catch (err) {
        console.error('Erro ao excluir produto:', err);
        alert('Não foi possível excluir o produto.');
      }
    }
  };
  
  // Formatar preço para exibição
  const formatPrice = (price: number | null) => {
    if (price === null) return 'Preço não informado';
    return `R$ ${price.toFixed(2).replace('.', ',')}`;
  };
  
  // Renderizar o formulário de adição/edição
  const renderForm = () => (
    <div className={`p-4 rounded-lg shadow-md mb-6 ${theme === 'dark' ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'}`}>
      <h2 className={`text-xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
        {editingProduct ? 'Editar Produto' : 'Adicionar Novo Produto'}
      </h2>
      
      <form onSubmit={editingProduct ? handleUpdateProduct : handleAddProduct}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Jogo*
            </label>
            {editingProduct ? (
              // Se estiver editando, mostrar apenas o nome do jogo selecionado
              <div className={`px-3 py-2 border rounded-md ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-300'}`}>
                {gameOptions.find(g => g.id === formData.game_id)?.name || 'Jogo selecionado'}
              </div>
            ) : (
              // Se estiver adicionando, mostrar o campo de busca e seleção
              <div>
                <div className="relative mb-2">
                  <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`} />
                  <input
                    type="text"
                    placeholder="Buscar jogo..."
                    value={gameSearchText}
                    onChange={(e) => setGameSearchText(e.target.value)}
                    className={`pl-10 pr-3 py-2 w-full border rounded-md ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-indigo-400 focus:border-indigo-500' : 'border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-indigo-500 focus:border-indigo-500'}`}
                  />
                </div>
                
                {loadingGames ? (
                  <div className="text-center p-2">
                    <div className={`animate-spin inline-block h-4 w-4 border-t-2 border-b-2 rounded-full ${theme === 'dark' ? 'border-indigo-400' : 'border-indigo-500'}`}></div>
                  </div>
                ) : gameOptions.length > 0 ? (
                  <select
                    name="game_id"
                    value={formData.game_id}
                    onChange={handleFormChange}
                    className={`w-full px-3 py-2 border rounded-md ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:ring-indigo-400' : 'border-gray-300 text-gray-900 focus:ring-indigo-500'}`}
                    required={!editingProduct}
                  >
                    <option value="">Selecione um jogo</option>
                    {gameOptions.map(game => (
                      <option key={game.id} value={game.id}>
                        {game.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className={`text-sm p-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Digite o nome de um jogo para buscar
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="md:col-span-2">
            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              URL do Produto*
            </label>
            <input
              type="url"
              name="product_url"
              value={formData.product_url}
              onChange={handleFormChange}
              className={`w-full px-3 py-2 border rounded-md ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:ring-indigo-400' : 'border-gray-300 text-gray-900 focus:ring-indigo-500'}`}
              required
            />
          </div>
          
          <div>
            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Preço (R$)
            </label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleFormChange}
              step="0.01"
              min="0"
              className={`w-full px-3 py-2 border rounded-md ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-indigo-400' : 'border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-indigo-500'}`}
              placeholder="Ex: 249.90"
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              name="available"
              checked={formData.available}
              onChange={handleFormChange}
              className={`h-4 w-4 rounded focus:ring-indigo-500 ${theme === 'dark' ? 'text-indigo-500 border-gray-600 bg-gray-700 focus:ring-offset-gray-800' : 'text-indigo-600 border-gray-300'}`}
            />
            <label htmlFor="available" className={`ml-2 block text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
              Produto disponível
            </label>
          </div>
        </div>
        
        <div className="mt-4 flex justify-end space-x-2">
          <button
            type="button"
            onClick={resetForm}
            className={`px-4 py-2 rounded-md ${theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
          >
            Cancelar
          </button>
          
          <button
            type="submit"
            className={`px-4 py-2 rounded-md ${theme === 'dark' ? 'bg-indigo-500 text-white hover:bg-indigo-600' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
          >
            {editingProduct ? 'Atualizar' : 'Adicionar'}
          </button>
        </div>
      </form>
    </div>
  );
  
  // Renderizar controles de paginação
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="flex justify-center mt-6">
        <nav className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded-md ${
              currentPage === 1
                ? theme === 'dark' ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Anterior
          </button>
          
          <span className={`px-3 py-1 rounded-md ${theme === 'dark' ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'}`}>
            {currentPage}
          </span>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded-md ${
              currentPage === totalPages
                ? theme === 'dark' ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Próximo
          </button>
        </nav>
      </div>
    );
  };
  
  if (loading && !store) { // Mostrar loader principal se a loja ainda não carregou
    return (
      <MainLayout>
        <div className={`container mx-auto px-4 py-6 flex justify-center items-center min-h-screen ${theme === 'dark' ? 'bg-background text-foreground' : 'bg-gray-50 text-gray-900'}`}>
          <Loader />
        </div>
      </MainLayout>
    );
  }
  
  return (
    <MainLayout>
      <SEO 
        title={`Produtos da ${store?.name || 'Loja'} - Boardverso`}
        description={`Lista de jogos disponíveis na loja ${store?.name || 'desconhecida'}`}
      />
      <Toaster 
        position="top-right"
        toastOptions={{
          className: '',
          style: {
            background: theme === 'dark' ? '#333' : '#fff',
            color: theme === 'dark' ? '#fff' : '#000',
          },
        }}
      />
      
      <div className={`container mx-auto px-4 py-6 max-w-7xl ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
        <div className="flex items-center mb-6">
          <Link
            to="/stores"
            className={`mr-3 p-2 rounded-full ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`}
            title="Voltar para a lista de lojas"
          >
            <ArrowLeft size={20} />
          </Link>
          
          <div className="flex flex-col sm:flex-row w-full items-start sm:items-center justify-between">
            <div className="flex items-center">
              <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>Produtos da {store?.name || 'Loja'}</h1>
              {store?.logo_url && (
                <img 
                  src={store.logo_url} 
                  alt={`Logo ${store.name}`} 
                  className="h-8 w-auto ml-3"
                />
              )}
            </div>
            
            {isAdmin && (
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className={`flex items-center justify-center px-4 py-2 rounded-md mt-2 sm:mt-0 ${theme === 'dark' ? 'bg-indigo-500 text-white hover:bg-indigo-600' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
              >
                {showAddForm ? 'Cancelar Adição' : (
                  <>
                    <Plus size={18} className="mr-1" />
                    Adicionar Produto
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        
        {isAdmin && showAddForm && renderForm()}
        
        <div className="relative mb-6">
          <input
            type="text"
            placeholder="Buscar produtos..."
            value={searchTerm}
            onChange={handleSearchChange}
            className={`w-full px-4 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-2 ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-indigo-400' : 'border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-indigo-500'}`}
          />
          <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`} />
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <Loader />
          </div>
        ) : error ? (
          <div className={`text-center my-12 ${theme === 'dark' ? 'text-red-400' : 'text-red-500'}`}>{error}</div>
        ) : filteredProducts.length === 0 ? (
          <div className={`text-center my-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            {searchTerm ? 'Nenhum produto encontrado com este termo.' : 'Nenhum produto cadastrado nesta loja.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <div 
                key={product.id}
                className={`rounded-lg shadow overflow-hidden ${!product.available ? 'opacity-60' : ''} ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}
              >
                <Link 
                    to={product.product?.slug ? `/game-prices/slug/${product.product.slug}` : (product.product?.id ? `/game-prices/${product.product.id}` : '#')} 
                    className="block"
                    title={product.product?.name || 'Ver detalhes do jogo'}
                >
                  <div className={`h-48 overflow-hidden ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    {product.product?.image_url ? (
                      <img 
                        src={product.product.image_url} 
                        alt={product.product.name || 'Imagem do Jogo'} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/placeholder-game.jpg';
                        }} 
                      />
                    ) : (
                      <img 
                        src="/placeholder-game.jpg" 
                        alt="Imagem não disponível" 
                        className="w-full h-full object-cover" 
                      />
                    )}
                  </div>
                </Link>

                <div className="p-4">
                  <Link 
                    to={product.product?.slug ? `/game-prices/slug/${product.product.slug}` : (product.product?.id ? `/game-prices/${product.product.id}` : '#')} 
                    className="block"
                    title={product.product?.name || 'Ver detalhes do jogo'}
                  >
                    <h3 className={`font-semibold text-lg mb-2 truncate transition-colors ${theme === 'dark' ? 'text-white hover:text-indigo-400' : 'text-gray-800 hover:text-indigo-600'}`}>
                      {product.product?.name || 'Produto sem nome'}
                    </h3>
                  </Link>

                  <div className="flex justify-between items-center">
                    <div>
                      <p className={`text-xl font-bold ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`}>{formatPrice(product.price)}</p>
                      {!product.available && <p className={`text-xs ${theme === 'dark' ? 'text-red-400' : 'text-red-500'}`}>Indisponível</p>}
                    </div>
                    
                    <a 
                      href={product.product_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`flex items-center px-3 py-2 rounded-md text-sm ${theme === 'dark' ? 'bg-indigo-500 text-white hover:bg-indigo-600' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                      title="Ver produto na loja (abre em nova aba)"
                    >
                      Ver na loja <ExternalLink size={14} className="ml-1" />
                    </a>
                  </div>
                  
                  {isAdmin && (
                    <div className="flex justify-end mt-3 space-x-2">
                      <button
                        onClick={() => setupEditForm(product)}
                        className={`${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-700'}`}
                        title="Editar produto"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={(e) => handleDeleteProduct(e, product.id)}
                        className={`${theme === 'dark' ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-700'}`}
                        title="Excluir produto"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {renderPagination()}
      </div>
    </MainLayout>
  );
};

export default StoreProductsPage;